// What the Progress screen shows, worked out from the year payload. Pure: no
// React, no clock, no I/O - the same split `day/day-model.ts`,
// `month/month-model.ts` and `shell/screens.ts` keep.
//
// NOTHING HERE DECIDES WHAT WAS FINISHED. That is the server's answer, counted
// from the completion log, and the rule it counts by is one function there
// (`thingKey` in `api/src/domain/year.ts`) so that two clients cannot phrase it
// two ways. What is here is how a year of counts becomes a 53-by-7 lattice, what
// each tile's shade is, and the sentences the screen says back to you.
//
// COUNTS, NEVER STREAKS. There is no run-length anywhere in this file, and there
// must not be: a missed day is a PALE SQUARE, not a broken chain (AGENTS.md).
// The tiles are deliberately built so that a gap has no shape of its own - it is
// the same square as every other quiet day, at the same weight.

import type { IsoDay, IsoYear, YearResponse, YearSchedule } from '../api/client.ts';
import { addDays, dayAndMonth, monthName, parseIsoDay, weekdayIndex } from '../iso-day.ts';
import { wording } from '../shell/voice.ts';
import type { Voice } from '../shell/voice.ts';

/**
 * The width of the lattice, in weeks.
 *
 * Fifty-three, because a 365-day year laid out in Sunday-first columns spans 53
 * of them whenever it does not start on a Sunday - and a 366-day one starting on
 * a Saturday spans 54. The last column of a 54-week year is the 31st of December
 * alone, and it is dropped rather than the whole grid being re-proportioned for
 * that one day: `GRID_WEEKS` is what the stylesheet's `repeat(53, …)` is, and
 * the two must be one number. See `gridTiles`, which reports the drop rather
 * than hiding it.
 *
 * THE NEXT SUCH YEAR IS 2028, and they come round every 28 years (2000, 2028,
 * 2056). Worth saying plainly, because "a leap year that opens on a Saturday"
 * sounds like a problem for somebody else's decade and this one is two years
 * out.
 */
export const GRID_WEEKS = 53;

/** The rows of the lattice: Sunday through Saturday, as the prototype draws it. */
export const GRID_DAYS = 7;

/** How much of one tile is filled in. `0` is the pale square a quiet day gets. */
export type TileLevel = 0 | 1 | 2 | 3;

/**
 * One cell of the lattice.
 *
 * `day` is null for the cells that are not days of this year at all - the ones
 * before 1 January and after 31 December, which exist because the lattice starts
 * on a Sunday and ends on a Saturday. They are drawn like a future day: present,
 * so the grid is a rectangle, and quieter than a day that simply had nothing on
 * it.
 */
export interface Tile {
  /** The day this cell is, or null when it falls outside the year. */
  day: IsoDay | null;
  /** Things finished on it. Zero for a quiet day and for a cell with no day. */
  finished: number;
  level: TileLevel;
  /** Outside the year, or after the year's own today. Drawn fainter. */
  faint: boolean;
}

/**
 * The shade a day's count is drawn at.
 *
 * Four levels and no more, which is the prototype's own scale (`.l1` / `.l2` /
 * `.l3` over a pale base) - the point of the grid is the SHAPE of a year at a
 * glance, and a continuous ramp reads as noise at 6px a tile. Three or more is
 * the top level rather than the scale being stretched to whatever the busiest
 * day held, because a single ten-item day would otherwise wash out every other
 * day of the year.
 */
export function levelFor(finished: number): TileLevel {
  if (finished <= 0) return 0;
  if (finished === 1) return 1;
  if (finished === 2) return 2;
  return 3;
}

/** The Sunday on or before a day - where the lattice's first column begins. */
export function weekStart(day: IsoDay): IsoDay {
  const index = weekdayIndex(day);
  return index === null ? day : addDays(day, -index);
}

export interface Lattice {
  /**
   * Every cell, in COLUMN order: seven days of week one, then seven of week
   * two. That is the order a `grid-auto-flow: column` grid consumes, and it is
   * the order the prototype builds, so a week is a column and a weekday is a
   * row.
   */
  tiles: Tile[];
  /** The column each month's first week starts in, 1-based, for the labels. */
  monthColumns: { month: number; column: number }[];
  /**
   * Days of the year the lattice could not fit, which is 0 for every year but
   * the rare 366-day one that starts on a Saturday.
   *
   * Reported rather than silently dropped: a grid that quietly loses the last
   * day of a year is the kind of thing nobody notices for a decade, and the
   * screen states it in the card's own words when it happens.
   *
   * Never more than `GRID_DAYS`: 371 cells hold every year with at most a day
   * to spare, so a bigger figure than a week could only come from a span that is
   * not a pair of calendar days at all.
   */
  dropped: number;
}

/**
 * The lattice for one schedule: 53 columns of 7 days, starting on the Sunday on
 * or before the first of January.
 *
 * `view.firstDay` and `view.lastDay` come from the server, so which days the
 * year holds is not this client's answer - what is here is only how they are
 * arranged. `days` on the schedule is SPARSE, so this walks the lattice and
 * looks each day up rather than walking the payload: a quiet day is the ordinary
 * case, and it must cost the same as a busy one to draw.
 */
export function gridTiles(view: YearResponse, schedule: YearSchedule): Lattice {
  const finishedOn = new Map(schedule.days.map((entry) => [entry.day, entry.finished]));

  const tiles: Tile[] = [];
  const monthColumns: { month: number; column: number }[] = [];
  const seenMonths = new Set<number>();

  let day = weekStart(view.firstDay);
  let covered = false;

  for (let week = 0; week < GRID_WEEKS; week += 1) {
    for (let row = 0; row < GRID_DAYS; row += 1) {
      const inYear = day >= view.firstDay && day <= view.lastDay;
      const finished = inYear ? (finishedOn.get(day) ?? 0) : 0;
      // A day after the year's own today has not happened; a cell outside the
      // year is not a day of it at all. Both are drawn fainter than a day that
      // simply had nothing on it, which is the distinction the day screen makes
      // when it draws no bar on a future day: a plan is not a failure.
      const faint = !inYear || day > view.today;

      tiles.push({ day: inYear ? day : null, finished, level: levelFor(finished), faint });

      // THE FIRST COLUMN THAT BEGINS IN THIS MONTH - the cell at row 0, which is
      // the Sunday the column starts on, and only while that Sunday is still in
      // the month's first week. A column that merely ENDS in the month is mostly
      // the month before it, so a label there would sit over the wrong stretch of
      // the year; and the `<= GRID_DAYS` bound is what stops a Sunday late in
      // August claiming September's label.
      const parts = inYear ? parseIsoDay(day) : null;
      if (parts !== null && row === 0 && !seenMonths.has(parts.month) && parts.day <= GRID_DAYS) {
        seenMonths.add(parts.month);
        monthColumns.push({ month: parts.month, column: week + 1 });
      }

      if (day === view.lastDay) covered = true;
      day = addDays(day, 1);
    }
  }

  // `day` now stands one past the lattice. Anything between it and the last day
  // of the year did not fit - see `dropped` above, which is where the week this
  // stops counting at is stated. `addDays` hands a value it cannot parse back
  // unchanged, deliberately and for other screens' sake, so the bound is the
  // difference between a wrong figure and a tab that never paints again.
  let dropped = 0;
  if (!covered) {
    for (let extra = day; extra <= view.lastDay && dropped < GRID_DAYS; extra = addDays(extra, 1)) {
      dropped += 1;
    }
  }

  return { tiles, monthColumns, dropped };
}

/**
 * The months that get a label under a grid.
 *
 * EVERY OTHER ONE. Twelve labels across 53 columns is about 9px of space each on
 * a phone, which is a smudge rather than a reading; the prototype drops the odd
 * months for exactly this reason and keeps January, March, May and the rest.
 */
export function labelledMonths(lattice: Lattice): { label: string; column: number }[] {
  return lattice.monthColumns
    .filter((entry) => entry.month % 2 === 1)
    .map((entry) => ({
      // `monthName` takes a day, so the first of that month in any year names
      // it - the year is irrelevant to the word and this file has no clock.
      label: monthName(`2000-${String(entry.month).padStart(2, '0')}-01`).slice(0, 3),
      column: entry.column,
    }));
}

function things(n: number): string {
  return `${n} thing${n === 1 ? '' : 's'}`;
}

function days(n: number): string {
  return `${n} day${n === 1 ? '' : 's'}`;
}

/**
 * The sentence the year answers itself with, before a single tile is drawn.
 *
 * "What did I finish this year" is the question this screen exists for, and the
 * approved design answers it in words first and in charts second
 * (prototype-v1.md section 6). Both figures are the server's; the grammar is
 * here, for the same reason `monthSummary` and `emptyMonthBody` are in
 * `month-model.ts`.
 */
export function yearHeadline(view: YearResponse, voice: Voice): string {
  // "YET" ONLY WHILE THERE IS STILL TIME. A year that has already ended is not
  // going to gain anything, so "Nothing finished in 2025 yet." promises a change
  // that cannot come - and a year the app did not exist for is the FIRST thing
  // anybody pressing the back arrow sees.
  //
  // PLAIN IN BOTH VOICES, and the only line on this screen that is. The approved
  // pack has no pair for it, and an empty year is the one place on the screen
  // where a person may be looking at a year they are disappointed by - which is
  // exactly where the casual voice does damage rather than work.
  if (view.finished === 0) return `Nothing finished in ${view.year}${isOver(view) ? '' : ' yet'}.`;
  return wording('year.headline', voice, {
    things: things(view.finished),
    year: view.year,
    days: days(view.activeDays),
  });
}

/** Whether the whole year is behind the server's own today. */
export function isOver(view: YearResponse): boolean {
  return view.lastDay < view.today;
}

/**
 * A year with nothing in it at all.
 *
 * Said plainly, and without pointing at a control: the + is the day screen's on
 * a phone and the sidebar's on a desktop, so naming one of them here would name
 * a button that is not on screen at the other width - the dead end this client
 * has been caught by five times (web/src/README.md).
 *
 * A year that is OVER gets its own sentence, because the reassurance the current
 * year's carries - that this fills in as you go - is a promise a finished year
 * cannot keep. Neither scolds: a quiet year is a quiet year, and there is no
 * voice in this product for telling somebody off about one.
 */
export function emptyYearBody(view: YearResponse): string {
  if (isOver(view)) {
    return (
      `This screen reads back what you ticked off, and there is nothing from ${view.year}. ` +
      'Every year you use Perfect Note keeps its own page here.'
    );
  }
  return (
    'Everything you tick off is counted from the moment you do it, so this fills in on its own. ' +
    'Nothing is waiting to be switched on.'
  );
}

/**
 * "August", from the busiest-month figure, or null when there is no such month.
 *
 * The MONTH ALONE, without its year: the figure sits under a heading that names
 * the year already, and every month in it is in that year by construction.
 */
export function busiestMonthName(view: YearResponse): string | null {
  if (view.busiestMonth === null) return null;
  return monthName(`${view.busiestMonth.month}-01`);
}

/** Whether the year on screen is the one the server's own today falls in. */
export function isThisYear(view: YearResponse): boolean {
  return view.today >= view.firstDay && view.today <= view.lastDay;
}

/**
 * The line at the foot of one schedule's card.
 *
 * An ARCHIVED schedule says so and says its history is kept, because archiving
 * frees a slot and keeps the year (AGENTS.md) and somebody looking at a tagged
 * card is entitled to be told that in words rather than by the tiles still being
 * there. A schedule that no longer exists at all says that instead - it is the
 * one card on this screen that opens nothing, and a card you cannot act on with
 * nothing to explain it reads as a bug.
 *
 * Otherwise it is a RATE, which is the prototype's own footer: a figure that
 * only grows, said in the units a person keeps a habit in. It is deliberately
 * not a percentage of anything - there is no denominator here that is not a
 * recomputation of what was due, and this screen does not recompute.
 */
export function scheduleNote(view: YearResponse, schedule: YearSchedule): string {
  if (schedule.id === null) {
    return 'This schedule has been deleted. What you finished in it stays here.';
  }
  if (schedule.archived) {
    const when = schedule.archivedDay === null ? '' : ` on ${dayAndMonth(schedule.archivedDay)}`;
    return `Archived${when}. Its history stays here.`;
  }

  const perWeek = weeklyRate(view, schedule);
  if (perWeek === null) return '';
  return `About ${perWeek} a week${isThisYear(view) ? ' so far' : ''}.`;
}

/**
 * Roughly how many a week, over the part of the year that has happened.
 *
 * Measured to TODAY rather than to the last day of December, or a year three
 * months old would report a third of its real rate. A past year is measured
 * across the whole of itself. Null when the span is too short to divide by, and
 * when the rate would round to nothing - "About 0 a week" is a sentence with no
 * information in it and a faintly accusing one at that.
 */
export function weeklyRate(view: YearResponse, schedule: YearSchedule): number | null {
  const end = isThisYear(view) ? view.today : view.lastDay;
  const elapsed = daysBetween(view.firstDay, end) + 1;
  if (elapsed < GRID_DAYS) return null;
  const rate = Math.round(schedule.finished / (elapsed / GRID_DAYS));
  return rate < 1 ? null : rate;
}

/** Whole days from `a` to `b`, both server-named. String arithmetic, no clock. */
function daysBetween(a: IsoDay, b: IsoDay): number {
  const from = parseIsoDay(a);
  const to = parseIsoDay(b);
  if (from === null || to === null) return 0;
  const ms =
    Date.UTC(to.year, to.month - 1, to.day) - Date.UTC(from.year, from.month - 1, from.day);
  return Math.round(ms / 86_400_000);
}

/**
 * What one schedule's grid says to a screen reader.
 *
 * ONE LABEL FOR THE WHOLE PICTURE, rather than 371 cells each announcing a date
 * and a number: a year grid read out cell by cell is twenty minutes of "nothing
 * due". The card's own heading already carries the schedule and its total, so
 * this says what the picture ADDS - which days of the year it covers - and the
 * figures stay where they are readable.
 */
export function gridLabel(view: YearResponse, schedule: YearSchedule): string {
  return (
    `${schedule.name}: every day of ${view.year}. ` +
    `${things(schedule.finished)} finished, on ${days(schedule.days.length)}.`
  );
}

/**
 * The year a day the SERVER named falls in, for the control that goes back to
 * this one.
 *
 * Never used to work out which year it IS - that is the time-zone question,
 * asked as the literal `'this'` and answered on the server. This only slices a
 * day that has already been answered, exactly as `monthOfDay` does next door.
 */
export function yearOfDay(day: IsoDay): IsoYear {
  return day.slice(0, 4);
}
